// pages/module-pages/candidatures/CandidaturesPage.tsx
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, X, Eye, Download, CheckCircle, XCircle, Clock } from "lucide-react";
import PageLayout from "../../../layouts/PageLayout";
import CandidaturesList from "../../../components/shared/candidatures/CandidaturesList";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import useCandidatures from "../../../hooks/features/candidatures/useCandidatures";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { Candidature } from "../../../types/features/Candidature";
import { useProduct } from "../../../hooks/core/useProduct";
import { MAIN_URL } from "../../../config/api";

export default function CandidaturesPage() {
  const navigate = useNavigate();
  const { selectedProduct } = useProduct();
  const { candidatures, updateCandidature, deleteCandidature, loading } = useCandidatures();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCandidature, setSelectedCandidature] = useState<Candidature | null>(null);
  const [candidatureToDelete, setCandidatureToDelete] = useState<Candidature | null>(null);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ✅ Filtrer les candidatures par produit (hérité de l'offre d'emploi)
  const candidaturesByProduct = useMemo(() => {
    if (selectedProduct && selectedProduct !== 'all') {
      return candidatures.filter((c) => c.job_offer?.product_name === selectedProduct);
    }
    return candidatures;
  }, [candidatures, selectedProduct]);

  // ✅ Stats basées sur les candidatures filtrées par produit
  const stats = {
    total: candidaturesByProduct.length,
    pending: candidaturesByProduct.filter((c) => c.status === "pending").length,
    reviewed: candidaturesByProduct.filter((c) => c.status === "reviewed").length,
    accepted: candidaturesByProduct.filter((c) => c.status === "accepted").length,
    rejected: candidaturesByProduct.filter((c) => c.status === "rejected").length,
  };

  // ✅ Filtrer les candidatures (recherche + statut) à partir des candidatures déjà filtrées par produit
  const filteredCandidatures = useMemo(() => {
    let result = candidaturesByProduct;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.full_name.toLowerCase().includes(searchLower) ||
          c.email.toLowerCase().includes(searchLower) ||
          c.phone?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedStatus) {
      result = result.filter((c) => c.status === selectedStatus);
    }

    return result;
  }, [candidaturesByProduct, searchTerm, selectedStatus]);

  const handleStatusChange = async (candidature: Candidature, status: string) => {
    const success = await updateCandidature(candidature.id, status);
    if (success) {
      alertSuccess(
        `Candidature ${status === "accepted" ? "acceptée" : status === "rejected" ? "rejetée" : "examinée"}`
      );
    } else {
      alertError("Erreur lors de la mise à jour");
    }
    setSelectedCandidature(null);
  };

  const handleDelete = async () => {
    if (!candidatureToDelete) return;
    setIsDeleting(true);
    const success = await deleteCandidature(candidatureToDelete.id);
    setIsDeleting(false);
    if (success) {
      setCandidatureToDelete(null);
      alertSuccess("Candidature supprimée avec succès");
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    let success = true;
    for (const id of itemsToDelete) {
      const result = await deleteCandidature(id);
      if (!result) success = false;
    }
    if (success) {
      setItemsToDelete(null);
      alertSuccess(`${itemsToDelete.length} candidature(s) supprimée(s)`);
    } else {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const handleDownloadCV = (candidature: Candidature) => {
    if (candidature.cv_path) {
      window.open(`${MAIN_URL}/storage/${candidature.cv_path}`, "_blank");

    }
  };

  const clearFilters = () => {
    setSelectedStatus("");
    setSearchTerm("");
  };

  const hasActiveFilters = selectedStatus || searchTerm;

  const getActionsMenu = (candidature: Candidature): Menu[] => {
    return [
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => navigate(`/admin/candidatures/${candidature.id}`),
      },
      {
        label: "Télécharger CV",
        icon: Download,
        onClick: () => handleDownloadCV(candidature),
      },
      {
        label: "Marquer comme examinée",
        icon: Clock,
        onClick: () => handleStatusChange(candidature, "reviewed"),
      },
      {
        label: "Accepter",
        icon: CheckCircle,
        onClick: () => handleStatusChange(candidature, "accepted"),
      },
      {
        label: "Rejeter",
        icon: XCircle,
        onClick: () => handleStatusChange(candidature, "rejected"),
      },
      {
        label: "Supprimer",
        icon: XCircle,
        onClick: () => setCandidatureToDelete(candidature),
      },
    ];
  };

  return (
    <PageLayout title="Gestion des candidatures" className="min-h-screen bg-gray-50">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Candidatures</h1>
              <p className="text-sm text-gray-500 mt-1">Gérez les candidatures reçues pour les offres d'emploi</p>
            </div>
          </div>

          {/* Indicateur de filtre produit */}
          {selectedProduct && selectedProduct !== 'all' && (
            <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-700">
              Filtré par produit : <span className="font-semibold">{selectedProduct}</span>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
              <p className="text-xs text-gray-700/70">Total</p>
              <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4">
              <p className="text-xs text-yellow-700/70">En attente</p>
              <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <p className="text-xs text-blue-700/70">Examinées</p>
              <p className="text-2xl font-bold text-blue-700">{stats.reviewed}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <p className="text-xs text-green-700/70">Acceptées</p>
              <p className="text-2xl font-bold text-green-700">{stats.accepted}</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4">
              <p className="text-xs text-red-700/70">Rejetées</p>
              <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
            </div>
          </div>

          {/* Recherche */}
          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un candidat..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Filtres */}
          <div className="mt-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              <Filter size={18} /> Filtrer
              {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
            </button>

            {showFilters && (
              <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">Filtres</h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <X size={14} /> Effacer tout
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Tous</option>
                      <option value="pending">En attente</option>
                      <option value="reviewed">Examinée</option>
                      <option value="accepted">Acceptée</option>
                      <option value="rejected">Rejetée</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <CandidaturesList
            candidatures={filteredCandidatures}
            onAction={(candidature) => setSelectedCandidature(candidature)}
            onSelectCandidatures={(selected) => setItemsToDelete(selected.map((c) => c.id))}
            selectable={true}
            selectActions={[
              {
                label: "Supprimer",
                onClick: (selected) => setItemsToDelete(selected.map((c) => c.id)),
                className: "bg-red-600 text-white",
              },
            ]}
          />
        )}
      </div>

      {/* Menu modal */}
      {selectedCandidature && (
        <MenuModal
          title={selectedCandidature.full_name}
          isOpen={!!selectedCandidature}
          onClose={() => setSelectedCandidature(null)}
          icon={null}
          menu={getActionsMenu(selectedCandidature)}
        />
      )}

      {/* Modals suppression */}
      <DeleteConfirmationModal
        isOpen={!!candidatureToDelete}
        onClose={() => setCandidatureToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer la candidature"
        message={`Supprimer la candidature de "${candidatureToDelete?.full_name}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />

      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} candidature(s) ?`}
      />
    </PageLayout>
  );
}